/**
 * Kroner, in the three shapes the register actually uses.
 *
 * Decision 17 of the 15 Aug round, and it exists because ad-hoc formatting had
 * produced a share register where the same kind of figure was written two ways
 * — `kr 9,5` in one place and `kr 10` in another, both meaning nine kroner
 * fifty. Every krone figure now goes through one of these three, and the choice
 * between them says what kind of number it is:
 *
 *   fmtKrPrice  a rate — price per share, strike price. Always two decimals,
 *               because øre in a price are the difference between kr 9,50 and
 *               kr 9,05 and a share register is where that matters.
 *   fmtKrExact  what one person owes. Always two decimals, because it is the
 *               figure they will type into their bank.
 *   fmtKrSum    an aggregate — raised, moved, outstanding. Whole kroner,
 *               because two decimals on a seven-figure total is noise nobody
 *               reads and everybody has to scan past.
 *
 * USD is separate and unrelated: the stores report in dollars and decision 37
 * keeps them there. See `nf` for plain counts.
 */

/**
 * Which reader the figure is for. Defaults to `nb` everywhere, so every existing
 * call site is unchanged.
 *
 * Added 3 Sep 2026 with the bilingual share documents. `kr 9,50` is correct
 * Norwegian and a hazard in an English document — a comma decimal reads as a
 * thousands separator to half the world, and `kr 9,50` becoming nine hundred and
 * fifty on a document about somebody's money is the kind of mistake that is
 * discovered years later. So English gets `NOK 9.50`, and it gets there through
 * these same three functions rather than through a fourth one written locally:
 * decision 17 exists because two ways of writing a krone figure produced a
 * register that disagreed with itself, and a second language is not a reason to
 * reopen that.
 */
export type MoneyLang = 'nb' | 'en'

const LOCALE: Record<MoneyLang, string> = { nb: 'nb-NO', en: 'en-GB' }
/** Norwegian writes the symbol, English writes the ISO code — `kr` means nothing abroad. */
const UNIT: Record<MoneyLang, string> = { nb: 'kr', en: 'NOK' }

const amount = (n: number, decimals: number, lang: MoneyLang) =>
  n.toLocaleString(LOCALE[lang], {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })

/** A rate: price per share, strike price. `kr 9,50` / `NOK 9.50`. */
export const fmtKrPrice = (n: number, lang: MoneyLang = 'nb'): string =>
  `${UNIT[lang]} ${amount(n, 2, lang)}`

/** What somebody owes, to the øre. `kr 47 500,00` / `NOK 47,500.00`. */
export const fmtKrExact = (n: number, lang: MoneyLang = 'nb'): string =>
  `${UNIT[lang]} ${amount(n, 2, lang)}`

/** An aggregate. `kr 231 000` / `NOK 231,000`. */
export const fmtKrSum = (n: number, lang: MoneyLang = 'nb'): string =>
  `${UNIT[lang]} ${amount(Math.round(n), 0, lang)}`

/**
 * A typed amount, however it was typed.
 *
 * Norwegian keyboards produce a comma and the design says input still accepts
 * one; `Number('9,50')` is `NaN`, which turned a valid strike price into a
 * silently dropped field. Spaces go too — people paste `47 500` from a bank.
 */
export function parseKr(v: string): number | null {
  const cleaned = v.replace(/\s/g, '').replace(',', '.')
  if (!cleaned) return null
  const n = Number(cleaned)
  return Number.isFinite(n) ? n : null
}
